import React, { useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import * as graph from '@adc/sdk'
import * as blueprint from '@adc/blueprint'
import {
  ListingCard,
  ListingCardViewModeEnum,
  useApplicationContext,
  useApplicationModals,
  useApplicationNotifications,
  useApplicationQueues,
  useProgressiveCapture,
  useBuyingCriteriaModal,
} from '@adc/ui-adc'
import classnames from 'classnames/bind'
import statics from '../../event.statics'
import { trackSave } from '../../services/event.analytics'
import styles from './styles.scss'
const cx = classnames.bind(styles)

const SAVE_MESSAGES = {
  save: {
    title: 'Save Successful',
    message:
      "You'll now receive updates on this property. View all your saved properties on your dashboard.",
  },
  unsave: {
    title: 'Property Removed',
    message: 'The property is no longer saved on your dashboard.',
  },
}

const AssetsResults = ({ assets, user, featureFlags }) => {
  const showResults = assets && assets.length > 0
  const modals = useApplicationModals()
  const queues = useApplicationQueues()
  const notifications = useApplicationNotifications()

  // Progressive capture for buying preferences
  const ctx = useApplicationContext()
  const criteriaOptions =
    blueprint.user.hooks.useBuyingCriteriaOptionsFromContext({
      user: ctx?.user,
      updateUser: fields => ctx?.actions?.updateUser(fields),
    })
  const { data: criteriaData, actions: criteriaActions } =
    blueprint.user.hooks.useBuyingCriteria({
      ...criteriaOptions,
      autoSave: true,
    })

  const { openBuyingCriteriaModal } = useBuyingCriteriaModal({
    data: criteriaData,
    actions: criteriaActions,
  })

  const { nudgeAfterSave } = useProgressiveCapture({
    preferences: criteriaData?.criteria ?? null,
    preferencesOptions: criteriaOptions,
    onAnswerMore: openBuyingCriteriaModal,
    featureFlags,
  })

  const initialData = useMemo(
    () => (assets || []).map(listing => listing.primary_property),
    [assets]
  )
  const savedProperties = blueprint.user.hooks.useSavedProperties({
    initialData,
  })

  const toggleSave = useCallback(
    listing => {
      const saved = savedProperties.actions.isSaved(listing.primary_property)
      trackSave(saved ? 'unsave' : 'save', listing)
      return savedProperties.actions
        .toggleSaveProperty(listing.primary_property)
        .then(() => {
          notifications.overlayBanner?.open(
            SAVE_MESSAGES[saved ? 'unsave' : 'save']
          )
          if (!saved) nudgeAfterSave()
        })
    },
    [savedProperties, nudgeAfterSave, notifications]
  )

  // Saved-state drives the card heart via the controlled `isSaved` prop, so
  // listings pass through untouched (no merge). isSaved is the single source of
  // truth; the redux listing is never mutated on save.
  const { isSaved } = savedProperties.actions

  return (
    <div data-elm-id='event_assets_container'>
      {showResults && (
        <div
          data-elm-id='event_assets_table_properties'
          className={cx('cards-wrap')}
        >
          {assets.map((listing, i) => (
            <div key={listing.listing_id} className={cx('card', 'u-mb-3')}>
              <ListingCard
                elmId={`property_card_${i}`}
                listing={listing}
                isSaved={isSaved(listing.primary_property)}
                onSaveToggle={() => {
                  if (user.logged_in) {
                    toggleSave(listing)
                  } else {
                    queues.postAuthentication.push([
                      () => {
                        toggleSave(listing)
                      },
                    ])
                    modals.open('authentication', {
                      authInitialActive: statics.LOGIN,
                      authModalIntent: statics.SAVE_ASSET,
                    })
                  }
                }}
                showSave
                linkProps={{
                  href: listing.listing_page_path,
                }}
                showV2
                isCanCelled={
                  listing?.listing_status ===
                  graph.enums.ListingStatusGroup.Canceled
                }
                featureFlags={featureFlags}
                viewMode={ListingCardViewModeEnum.GRID}
              />
            </div>
          ))}
        </div>
      )}

      {!showResults && (
        <p
          data-elm-id='event_assets_table_no_results'
          className={cx('u-mt-2 u-mb-2')}
        >
          No properties available. To get more results, change some filters, or
          expand the area of your search.
        </p>
      )}
    </div>
  )
}

AssetsResults.propTypes = {
  assets: PropTypes.array.isRequired,
  user: PropTypes.object.isRequired,
  featureFlags: PropTypes.object,
}

export default AssetsResults
